import {
  Button,
  Image,
  LoadingOverlay,
  Menu,
  Stack,
  Tabs,
  TabsProps,
  Text,
  useMantineTheme,
} from '@mantine/core';
import { capitalize } from 'lodash/fp';
import React from 'react';

import {
  PurchasedProductType,
  usePurchasedProduct,
  useStoreSettings,
} from '@portals/api/organizations';
import { DetailsPanel, TextEditor, TextEditorProps } from '@portals/core';
import {
  DetailsList,
  DetailsListProps,
  PostPurchaseParamsDetailsList,
  ProductCategoryBadge,
  useOpenPolicyModal,
} from '@portals/framework';
import { ReactComponent as Gallery } from '@portals/icons/bold/gallery.svg';
import { ReactComponent as DocumentText } from '@portals/icons/linear/document-text.svg';
import { useOpenModal } from '@portals/redux';
import { prettyTime } from '@portals/utils';

import { PostPurchaseParametersModalProps } from '../../../../modals';

interface PurchasedProductDetailsPanelProps {
  purchasedProductId: string;
  onClose: () => void;
}

export function PurchasedProductDetailsPanel({
  purchasedProductId,
  onClose,
}: PurchasedProductDetailsPanelProps) {
  const theme = useMantineTheme();
  const openModal = useOpenModal();
  const openPolicyModal = useOpenPolicyModal();

  const storeSettings = useStoreSettings();

  const purchasedProduct = usePurchasedProduct(purchasedProductId);

  if (purchasedProduct.isFetching) {
    return <LoadingOverlay visible />;
  }

  return (
    <DetailsPanel
      enableScrollUiChanges={
        !purchasedProduct.data?.post_purchase_parameters_required
      }
    >
      <DetailsPanel.Header onClose={onClose} spacing="xxl">
        <Stack align="center">
          <Image
            width={100}
            height={100}
            radius="md"
            withPlaceholder
            src={purchasedProduct.data?.product?.image_url}
            placeholder={
              <Gallery
                width="50%"
                height="50%"
                color={theme.colors.blue_gray[2]}
              />
            }
          />

          <Stack spacing="xs" align="center">
            <DetailsPanel.Title>
              {purchasedProduct.data?.product?.name}
            </DetailsPanel.Title>

            {purchasedProduct.data?.product?.subtitle ? (
              <Text color="dimmed">
                {purchasedProduct.data?.product?.subtitle}
              </Text>
            ) : null}
          </Stack>
        </Stack>

        <Menu withinPortal width="target">
          <Menu.Target>
            <Button variant="default">Actions</Button>
          </Menu.Target>

          <Menu.Dropdown miw={198}>
            <Menu.Item
              icon={<DocumentText />}
              onClick={() =>
                openPolicyModal({
                  policyKey: 'terms_of_service',
                  editorContent: storeSettings.data?.terms_of_service,
                })
              }
            >
              Terms of use
            </Menu.Item>

            <Menu.Item
              icon={<DocumentText />}
              onClick={() =>
                openPolicyModal({
                  policyKey: 'privacy_policy',
                  editorContent: storeSettings.data?.privacy_policy,
                })
              }
            >
              Privacy
            </Menu.Item>

            <Menu.Item
              icon={<DocumentText />}
              onClick={() =>
                openPolicyModal({
                  policyKey: 'cancellations_and_returns_policy',
                  editorContent:
                    storeSettings.data?.cancellations_and_returns_policy,
                })
              }
            >
              Cancellation & returns
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>
      </DetailsPanel.Header>

      {purchasedProduct.data?.post_purchase_parameters_required ? (
        <Tabs defaultValue="overview" styles={tabsStyles} keepMounted={false}>
          <Tabs.List grow>
            <Tabs.Tab value="overview">Overview</Tabs.Tab>
            <Tabs.Tab value="moreDetails">More details</Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="overview">
            <DetailsPanel.Body>
              <OverviewTab purchasedProduct={purchasedProduct.data} />
            </DetailsPanel.Body>
          </Tabs.Panel>

          <Tabs.Panel value="moreDetails">
            <DetailsPanel.Body>
              {purchasedProduct.data?.post_purchase_parameters ? (
                <PostPurchaseParamsDetailsList
                  pppFields={purchasedProduct.data?.post_purchase_parameters}
                />
              ) : (
                <Stack align="flex-start">
                  <Text>
                    This product requires additional purchase information.
                    Please provide the missing information.
                  </Text>
                  <Button
                    variant="light"
                    onClick={() =>
                      openModal<PostPurchaseParametersModalProps['data']>(
                        'PostPurchaseParametersModal',
                        {
                          orderId: purchasedProduct.data?.order_id,
                        }
                      )
                    }
                  >
                    Complete Purchase Information
                  </Button>
                </Stack>
              )}
            </DetailsPanel.Body>
          </Tabs.Panel>
        </Tabs>
      ) : (
        <DetailsPanel.Body>
          <OverviewTab purchasedProduct={purchasedProduct.data} />
        </DetailsPanel.Body>
      )}
    </DetailsPanel>
  );
}

interface OverviewTabProps {
  purchasedProduct: PurchasedProductType | undefined;
}

function OverviewTab({ purchasedProduct }: OverviewTabProps) {
  const infoDetailsListItems: DetailsListProps['items'] = [
    {
      label: 'Category',
      value: purchasedProduct?.product?.category ? (
        <ProductCategoryBadge category={purchasedProduct?.product?.category} />
      ) : (
        '--'
      ),
    },
    {
      label: 'Product type',
      value: capitalize(purchasedProduct?.product?.product_type),
    },
    {
      label: 'Purchased at',
      value: prettyTime(purchasedProduct?.created_at),
    },
    {
      label: 'Seller',
      value: purchasedProduct?.seller?.display_name,
    },
    {
      label: 'ID',
      value: purchasedProduct?.id,
    },
  ];

  if (purchasedProduct?.sn) {
    infoDetailsListItems.push({
      label: 'Serial number',
      value: purchasedProduct?.sn,
    });
  }

  return (
    <Stack spacing={32}>
      <DetailsList title="Information" items={infoDetailsListItems} />

      <DetailsPanel.Section title="Description">
        <TextEditor
          styles={textEditorStyles}
          editorParams={{
            editable: false,
            content: purchasedProduct?.product?.description,
          }}
        />
      </DetailsPanel.Section>
    </Stack>
  );
}

const textEditorStyles: TextEditorProps['styles'] = (theme) => ({
  root: {
    border: 'none',
  },
  typographyStylesProvider: {
    fontSize: theme.fontSizes.sm,
    color: theme.colors.gray[8],
  },
  content: {
    '.ProseMirror': {
      paddingBlock: 0,
      paddingLeft: 0,
    },
  },
});

const tabsStyles: TabsProps['styles'] = () => ({
  root: {
    height: '100%',
  },
  tabsList: {
    marginInline: 32,
  },
  panel: {
    height: '100%',
  },
});
